import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { vonDataService } from '@/services/vonDataService';
import { Von02Resuscitation } from '@/types/vonData.types';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Save } from 'lucide-react';

interface Von02ResuscitationFormProps {
  patientId: number;
  onSaved?: () => void;
  readOnly?: boolean;
}

export const Von02ResuscitationForm: React.FC<Von02ResuscitationFormProps> = ({
  patientId,
  onSaved,
  readOnly = false
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [hasExistingData, setHasExistingData] = useState(false);

  // Form fields
  const [apgar1, setApgar1] = useState<string>('');
  const [apgar5, setApgar5] = useState<string>('');
  const [initialResus, setInitialResus] = useState(false);
  const [resusOxygen, setResusOxygen] = useState(false);
  const [resusFaceMask, setResusFaceMask] = useState(false);
  const [resusSupraglottic, setResusSupraglottic] = useState(false);
  const [resusEtTube, setResusEtTube] = useState(false);
  const [resusEpinephrine, setResusEpinephrine] = useState(false);
  const [resusCompressions, setResusCompressions] = useState(false);
  const [resusNasalVent, setResusNasalVent] = useState(false);
  const [resusNasalCpap, setResusNasalCpap] = useState(false);
  const [resusTemperature, setResusTemperature] = useState<string>('');
  const [measuredTemp, setMeasuredTemp] = useState<string>('');

  useEffect(() => {
    loadData();
  }, [patientId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await vonDataService.getResuscitation(patientId);

      if (data) {
        setHasExistingData(true);
        setApgar1(data.apgar_1?.toString() || '');
        setApgar5(data.apgar_5?.toString() || '');
        setInitialResus(Boolean(data.initial_resus));
        setResusOxygen(Boolean(data.resus_oxygen));
        setResusFaceMask(Boolean(data.resus_face_mask));
        setResusSupraglottic(Boolean(data.resus_supraglottic));
        setResusEtTube(Boolean(data.resus_et_tube));
        setResusEpinephrine(Boolean(data.resus_epinephrine));
        setResusCompressions(Boolean(data.resus_compressions));
        setResusNasalVent(Boolean(data.resus_nasal_vent));
        setResusNasalCpap(Boolean(data.resus_nasal_cpap));
        setResusTemperature(data.resus_temperature?.toString() || '');
        setMeasuredTemp(data.measured_temp?.toString() || '');
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (readOnly) return;

    try {
      setSubmitting(true);

      const formData: Partial<Von02Resuscitation> = {
        patient_id: patientId,
        apgar_1: apgar1 ? parseInt(apgar1) : undefined,
        apgar_5: apgar5 ? parseInt(apgar5) : undefined,
        initial_resus: initialResus,
        resus_oxygen: resusOxygen,
        resus_face_mask: resusFaceMask,
        resus_supraglottic: resusSupraglottic,
        resus_et_tube: resusEtTube,
        resus_epinephrine: resusEpinephrine,
        resus_compressions: resusCompressions,
        resus_nasal_vent: resusNasalVent,
        resus_nasal_cpap: resusNasalCpap,
        resus_temperature: resusTemperature ? parseInt(resusTemperature) : undefined,
        measured_temp: measuredTemp ? parseFloat(measuredTemp) : undefined
      };

      if (hasExistingData) {
        await vonDataService.updateResuscitation(patientId, formData);
      } else {
        await vonDataService.createResuscitation(patientId, formData);
        setHasExistingData(true);
      }

      toast({
        title: 'Succes',
        description: 'Datele au fost salvate cu succes'
      });

      if (onSaved) {
        onSaved();
      }
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut salva datele',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Secțiunea 02: Resuscitare inițială</CardTitle>
        <CardDescription>Date despre scorul APGAR și procedurile de resuscitare</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* APGAR scores */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Scor APGAR</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="apgar1">APGAR la 1 minut</Label>
                <Input
                  id="apgar1"
                  type="number"
                  min="0"
                  max="10"
                  value={apgar1}
                  onChange={(e) => setApgar1(e.target.value)}
                  placeholder="0-10"
                  disabled={readOnly}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="apgar5">APGAR la 5 minute</Label>
                <Input
                  id="apgar5"
                  type="number"
                  min="0"
                  max="10"
                  value={apgar5}
                  onChange={(e) => setApgar5(e.target.value)}
                  placeholder="0-10"
                  disabled={readOnly}
                />
              </div>
            </div>
          </div>

          {/* Resuscitation details */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Detalii resuscitare</h3>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="initialResus"
                checked={initialResus}
                onCheckedChange={(checked) => setInitialResus(checked === true)}
                disabled={readOnly}
              />
              <Label htmlFor="initialResus" className="text-sm font-normal cursor-pointer">
                Resuscitare inițială efectuată
              </Label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="resusOxygen"
                  checked={resusOxygen}
                  onCheckedChange={(checked) => setResusOxygen(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="resusOxygen" className="text-sm font-normal cursor-pointer">
                  Oxigen în sala de naștere
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="resusFaceMask"
                  checked={resusFaceMask}
                  onCheckedChange={(checked) => setResusFaceMask(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="resusFaceMask" className="text-sm font-normal cursor-pointer">
                  Ventilație cu mască facială
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="resusSupraglottic"
                  checked={resusSupraglottic}
                  onCheckedChange={(checked) => setResusSupraglottic(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="resusSupraglottic" className="text-sm font-normal cursor-pointer">
                  Dispozitiv supraglotic
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="resusEtTube"
                  checked={resusEtTube}
                  onCheckedChange={(checked) => setResusEtTube(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="resusEtTube" className="text-sm font-normal cursor-pointer">
                  Ventilație cu tub endotraheal
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="resusEpinephrine"
                  checked={resusEpinephrine}
                  onCheckedChange={(checked) => setResusEpinephrine(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="resusEpinephrine" className="text-sm font-normal cursor-pointer">
                  Epinefrină (sala naștere)
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="resusCompressions"
                  checked={resusCompressions}
                  onCheckedChange={(checked) => setResusCompressions(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="resusCompressions" className="text-sm font-normal cursor-pointer">
                  Compresii cardiace (sala naștere)
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="resusNasalVent"
                  checked={resusNasalVent}
                  onCheckedChange={(checked) => setResusNasalVent(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="resusNasalVent" className="text-sm font-normal cursor-pointer">
                  Ventilație nazală
                </Label>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="resusNasalCpap"
                  checked={resusNasalCpap}
                  onCheckedChange={(checked) => setResusNasalCpap(checked === true)}
                  disabled={readOnly}
                />
                <Label htmlFor="resusNasalCpap" className="text-sm font-normal cursor-pointer">
                  CPAP nazal
                </Label>
              </div>
            </div>
          </div>

          {/* Temperature */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium">Temperatură</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="resusTemp">Temperatură măsurată în prima oră de la internare</Label>
                <Select value={resusTemperature} onValueChange={setResusTemperature} disabled={readOnly}>
                  <SelectTrigger id="resusTemp">
                    <SelectValue placeholder="Selectează..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Nu</SelectItem>
                    <SelectItem value="1">Da</SelectItem>
                    <SelectItem value="9">N/A</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {resusTemperature === '1' && (
                <div className="space-y-2">
                  <Label htmlFor="measuredTemp">Valoare temperatură (°C)</Label>
                  <Input
                    id="measuredTemp"
                    type="number"
                    step="0.1"
                    value={measuredTemp}
                    onChange={(e) => setMeasuredTemp(e.target.value)}
                    placeholder="Celsius"
                    disabled={readOnly}
                  />
                </div>
              )}
            </div>
          </div>

          {!readOnly && (
            <div className="flex justify-end">
              <Button type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Se salvează...
                  </>
                ) : (
                  <>
                    <Save className="mr-2 h-4 w-4" />
                    Salvează
                  </>
                )}
              </Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
};
